import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import { editBulkLoad, getGeoShapeList, getAllEquipmentGroupList } from '../../actions/equipment-group';
import { getGeoStorageLocation } from '../../actions/geo';
import { editLogisticsLoad, getLogisticsHistory, updateLogisticsLoad } from '../../actions/logistics';
import { setIsLoadHistoryReload } from '../../actions/load';
import { Icon, Form, Heading6, Validator } from '../widgets';
import { Unit } from '../../helpers/unit';
import { FormValidator } from '../../helpers/form-validator';
import { capitalize, uuid } from '../../helpers/common';
import moment from 'moment-timezone';
import { toDisplayDateString } from '../../helpers/moment-ex';
import MultiSelect from '../../constants/MultiSelect';

const LoadForm = (props) => {
	const modalRef = useRef();
	const seedTypeList = useSelector((state) => state.logistics.logisticsHistory.seedType);
	const seedCuttingGroupList = useSelector((state) => state.logistics.logisticsHistory.seedCuttingGroup);
	const geoShapeList = useSelector(state => state.equipmentGroup.geoShapeList);
	const { weightShortName } = useSelector(state => state.user.settings);
	const operatorList = useSelector(state => state.person.operatorList);
	const newLoadIds = useSelector(state => state.equipmentGroup.loadHistory.newLoadIds || []);
	const sourceEGList = useSelector(state => state.equipmentGroup.harvesterList);
	const equipmentGroupList = useSelector(state => state.equipmentGroup.equipmentGroupList);
	const seedVarietyName = useSelector(state => state.logistics.logisticsHistory.logisticsVariety);
	const seedSupplierName = useSelector(state => state.logistics.logisticsHistory.logisticsSupplier);
	const seedLots = useSelector(state => state.logistics.logisticsHistory.logisticsLot);
	const selectedPlanType = useSelector(state => state.logistics.selectedPlanType);
	const allEGList = useSelector(state => state.equipmentGroup.allEG.list);
	const { range, custom: { from, to } } = useSelector(state => state.analyze.searchParams);
	const selectedPlanTypeId = useSelector(state => state.plan.selectedPlanType);
	const rateScheduleList = useSelector((state) => state.rateSchedule.rateScheduleList);

	const { load, farmId, show, onHide, isCloned } = props;

	const unit = Unit.getInstance();
	const dispatch = useDispatch();
	const unknownValue = "Unknown";
	const unknownOption = { value: -1, label: unknownValue };
	const isNewLoad = (loadId) => newLoadIds.includes(loadId);

	const allEG = useMemo(() => {
		if (allEGList && allEGList.length > 0) {
			return allEGList.filter((e) => e.primary_category_id && e.primary_category_id === 28).map((e) => {
				return {
					value: e.equipment_group_uuid,
					label: e.equipment_group_name
				}
			})
		}
		return [];
	}, [allEGList]);

	/* for dynamic fields */
	const farmSettings = useSelector(state => state.farm.farmSettings.farm_config);
	const { load_queue: { visible: dynamicFieldVisible, configuration: { load_queue_params = [] } = {} } = {} } = farmSettings || {};
	const [dynamicForm, setDynamicForm] = useState(null);
	const [showError, setShowError] = useState(false);

	const [formDetails, setFormDetails] = useState({
		loadActivityId: null,
		fieldId: null,
		srcEGId: null,
		sourceOperatorId: null,
		crewId: null,
		unloadSiteId: null,
		storageLocationId: null,
		tareWeight: 0,
		loadWeight: 0,
		grossWeight: 0,
		lightWeight: 0,
		loadTime: "",
		tarEGId: null,
		operatorId: null,
		transportEGId: "",
		transloadSiteId: "",
		transportOperatorId: "",
		pipe: null,
		pipeId: null,
		// seedVariety: null,
		// seedClass: null,
		// seedSupplier: null,
		// seedLot: null,
		load_params: null,
		// seedType: null,
		// seedCuttingGroup: null,
		// averageSeedSize: null,
		scale: [{ id: null, ticket: null, truck: null }],
		// sourceFieldId: null,
		sourceOffloadSite: null,
		sourceStorageLocationId: null,
		sourcePipe: null,
		sourcePipeId: null,
		rateScheduleId: null
	});
	const [storageLocations, setStorageLocations] = useState([]);
	const [storagePipes, setStoragePipes] = useState({});
	const [sourceStorageLocations, setSourceStorageLocations] = useState([]);
	const [sourceStoragePipes, setSourceStoragePipes] = useState({});
	const [isMultiTicket, setIsMultiTicket] = useState(false);

	useEffect(() => {
		if (show && allEGList && allEGList.length === 0) dispatch(getAllEquipmentGroupList({ farmId, selectedPlanType: 4 }));
	}, [show]);

	const getValidationRules = () => {
		return new FormValidator([
			{
				field: 'loadWeight',
				method: 'isEmpty',
				validWhen: false,
				message: 'Load weight required'
			},
			{
				field: 'loadWeight',
				method: 'isFloat',
				validWhen: true,
				args: [{ min: 0.1, max: 1000000 }],
				validateEmpty: false,
				message: 'Load weight between {min} and {max}'
			},
			{
				field: 'grossWeight',
				method: 'isEmpty',
				validWhen: false,
				message: 'Gross weight required'
			},
			{
				field: 'grossWeight',
				method: 'isFloat',
				validWhen: true,
				args: [{ min: 0.1, max: 1000000 }],
				validateEmpty: false,
				message: 'Gross weight should be between {min} and {max}'
			},
			{
				field: 'loadTime',
				method: "isSelected",
				validWhen: true,
				message: 'Load time is required'
			},
			{
				field: 'loadTime',
				method: "isMoment",
				validWhen: true,
				message: 'Invalid Load time'
			}
		]);
	};
	const validator = getValidationRules();
	const [isSaving, setIsSaving] = useState(false);
	const [validation, setValidation] = useState(validator.valid());
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [loadOptions, setLoadOptions] = useState({
		fieldOptions: [],
		unloadOptions: [],
		storageOptions: [],
		pipeOptions: [],
		operatorOptions: [],
		transloadoptions: [],
		targetEGOptions: [],
		transportEGOptions: [],
		transportOperatorOptions: [],
		seedLotOptions: [],
		seedClassOptions: [],
		crewOptions: [],
		seedVarietyOptions: [],
		seedTypeOptions: [],
		seedCuttingGroupOptions: [],
		scaleTruckOptions: [],
		sourceOffloadOptions: [],
		sourceStorageOptions: [],
		sourcePipeOptions: [],
	});

	useEffect(() => bindValues(load), [show]);

	const bindScale = (load) => {

		const targetEGId = load.target_equipment_group_uuid || null;
		const target = equipmentGroupList.find((e) => e.equipment_group_uuid === targetEGId);
		const primaryId = (target && target.primary_category_id) ? target.primary_category_id : null;
		setIsMultiTicket((primaryId && primaryId === 1) ? true : false);

		let scale = [{ id: uuid(), ticket: null, truck: null }];
		const { scale_ticket_list, truck_list, truck_name_list } = load;

		if (scale_ticket_list.length > truck_list.length) {
			scale = [];
			scale_ticket_list.forEach((e, i) => {
				const scaleObj = {
					id: uuid(),
					ticket: e,
					truck: (truck_list.length > 0 && truck_list[i]) ? truck_list[i] : null,
					truckName: (truck_name_list.length > 0 && truck_name_list[i]) ? truck_name_list[i] : null
				}
				scale.push(scaleObj);
			});
		} else if ((truck_list.length > scale_ticket_list.length) || (truck_list.length > 0 && truck_list.length === scale_ticket_list.length)) {
			scale = [];
			truck_list.forEach((e, i) => {
				const truckObj = {
					id: uuid(),
					ticket: (scale_ticket_list.length > 0 && scale_ticket_list[i]) ? scale_ticket_list[i] : null,
					truck: e,
					truckName: (truck_name_list.length > 0 && truck_name_list[i]) ? truck_name_list[i] : null,
				}
				scale.push(truckObj);
			});
		}
		return scale;
	}

	const getRateList = useMemo(() => {
		const defaultOptions = [{
			value: 'unknown_rate',
			label: "None"
		}];
		const rateList = rateScheduleList.filter((e) => e.rate_schedule_applicable === 1)
			.map(e => ({ label: e.rate_name, value: e.rate_uuid }));
		return (rateList && rateList.length > 0) ? [...defaultOptions, ...rateList] : [];
	}, [rateScheduleList]);

	const bindValues = (load) => {
		const scale = bindScale(load);

		setFormDetails({
			loadActivityId: load.load_activity_uuid,
			fieldId: !load.field_geo_shape_uuid || load.field_geo_shape_uuid === "field_unknown" ? unknownOption.value : load.field_geo_shape_uuid,
			srcEGId: !load.source_equipment_group_uuid || load.source_equipment_group_uuid === "implement_unknown" ? unknownOption.value : load.source_equipment_group_uuid,
			sourceOperatorId: !load.source_operator_uuid || load.source_operator_uuid === "operator_unknown" ? unknownOption.value : load.source_operator_uuid,
			crewId: !load.crew_uuid || load.crew_uuid === "crew_unknown" ? unknownOption.value : load.crew_uuid,
			unloadSiteId: !load.offload_geo_shape_uuid || load.offload_geo_shape_uuid === "offloadsite_unknown" ? unknownOption.value : load.offload_geo_shape_uuid,
			storageLocationId: !load.offload_geo_storage_location_uuid || load.offload_geo_storage_location_uuid === "storage_unknown" ? unknownOption.value : load.offload_geo_storage_location_uuid,
			tareWeight: unit.toWeight(load.tare_weight, 2),
			loadWeight: unit.toWeight(load.load_capacity, 2),
			lightWeight: unit.toWeight(load.light_weight, 2),
			grossWeight: load.load_gross_weight ? unit.toWeight(load.load_gross_weight, 2) : unit.toWeight(load.estimated_load_capacity, 2),
			operatorId: !load.operator_uuid || load.operator_uuid === "operator_unknown" ? unknownOption.value : load.operator_uuid,
			tarEGId: !load.target_equipment_group_uuid || load.target_equipment_group_uuid === "transport_unknown" ? unknownOption.value : load.target_equipment_group_uuid,
			loadTime: toDisplayDateString(load.load_date),
			transportEGId: !load.transport_equipment_group_uuid || load.transport_equipment_group_uuid === "transport_eg_unknown" ? unknownOption.value : load.transport_equipment_group_uuid,
			transloadSiteId: !load.transload_geo_shape_uuid || load.transload_geo_shape_uuid === "transloadsite_unknown" ? unknownOption.value : load.transload_geo_shape_uuid,
			transportOperatorId: !load.transport_operator_uuid || load.transport_operator_uuid === "operator_unknown" ? unknownOption.value : load.transport_operator_uuid,
			pipe: !load.pipe || load.pipe === unknownValue ? unknownOption.value : load.pipeId,
			pipeId: load.pipeId ? load.pipeId : null,
			// seedVariety: load.variety_name && load.variety_name !== unknownValue ? load.variety_name : null,
			// seedClass: load.seed_class && load.seed_class !== unknownValue ? load.seed_class : null,
			// seedSupplier: load.supplier && load.supplier !== unknownValue ? load.supplier : null,
			// seedLot: load && load.seed_lot && load.seed_lot !== unknownValue ? load.seed_lot : null,
			// seedType: (load && load.seed_type && load.seed_type !== unknownValue) ? load.seed_type : null,
			// seedCuttingGroup: (load && load.seed_cutting_group && load.seed_cutting_group !== unknownValue) ? load.seed_cutting_group : null,
			// averageSeedSize: (load && load.average_seed_size && load.average_seed_size !== unknownValue) ? load.average_seed_size : null,
			scaleTicket: (load && load.scale_ticket && load.scale_ticket !== unknownValue) ? load.scale_ticket.toString() : "",
			load_params: load.load_params ? load.load_params : null,
			scale,
			// sourceFieldId: !load.source_field_uuid ? unknownOption.value : load.source_field_uuid,
			sourceOffloadSite: load.source_offload_geo_shape_uuid && load.source_offload_geo_shape_uuid !== unknownValue ? load.source_offload_geo_shape_uuid : null,
			sourceStorageLocationId: load.source_offload_geo_storage_location_uuid && load.source_offload_geo_storage_location_uuid !== unknownValue ? load.source_offload_geo_storage_location_uuid : null,
			sourcePipe: !load.sourcePipe || load.sourcePipe === unknownValue ? unknownOption.value : load.sourcePipeId,
			sourcePipeId: load.sourcePipeId ? load.sourcePipeId : null,
			rateScheduleId: (load.rate_info && load.rate_info.rate_uuid) ? load.rate_info.rate_uuid : 'unknown_rate'
		});
	};

	const handleSelect = (options, id) => {
		let newFormDeatils = { ...formDetails, [id]: (options && options.value) ? options.value : null }

		if (id === "tarEGId" && options && options.value) {
			const target = equipmentGroupList.find((e) => e.equipment_group_uuid === options.value) || allEGList.find((e) => e.equipment_group_uuid === options.value);
			const existsScaleTruck = loadOptions.scaleTruckOptions.find((e) => e.equipment_group_uuid === options.value);
			if (target && target.vehicle) {
				const { primary_category_id } = target;
				if (primary_category_id && primary_category_id === 28) {
					setIsMultiTicket(true);
					newFormDeatils['scale'] = [{ id: uuid(), ticket: null, truck: null, truckName: null }];
				} else {
					if (target && target.vehicle && target.vehicle.is_truck) {
						const findTruck = scale.find((e) => e.truck && e.truck === options.value);
						if (options.value && findTruck && findTruck.truck) {
							newFormDeatils['scale'] = [findTruck];
						} else {
							newFormDeatils['scale'] = [{ id: uuid(), ticket: null, truck: options.value, truckName: options.label }];
						}
					} else {
						const truckId = (existsScaleTruck && existsScaleTruck.truck === options.value) ? options.value : null;
						const truckName = (existsScaleTruck && existsScaleTruck.truck === options.value) ? options.label : null;
						newFormDeatils['scale'] = [{ id: uuid(), ticket: null, truck: truckId, truckName }];
					}
					setIsMultiTicket(false);
				}
			} else {
				setIsMultiTicket(false);
				newFormDeatils['scale'] = [{ id: uuid(), ticket: null, truck: null, truckName: null }];
			}
		}

		setFormDetails(newFormDeatils);
	};

	useEffect(() => {
		if (dynamicFieldVisible && load_queue_params && load_queue_params.length > 0) {
			const tmp_fields = load_queue_params.map((e) => {
				return {
					...e,
					options: (Array.isArray(e.values) && e.values.length > 0)
						? e.values.map((elm) => {
							return { value: elm, label: elm }
						}) : []
				}
			})
			setDynamicForm(tmp_fields);
		}
	}, [dynamicFieldVisible, load_queue_params]);

	const checkValidation = (loadParams) => {
		let isValid = true;
		if (dynamicForm && dynamicForm.length > 0) {
			for (let i = 0; i < dynamicForm.length; i++) {
				const e = dynamicForm[i];
				if (!e) continue;
				if (e.required) {
					if (!loadParams || (loadParams && !loadParams[e.key])) {
						isValid = false;
						setShowError(true);
						break;
					}
				}

				if (loadParams && loadParams[e.key] && e.hasOwnProperty('rules') && e.rules.hasOwnProperty('max_length')
					&& loadParams[e.key].toString().trim().length > e.rules.max_length) {
					isValid = false;
					setShowError(true);
					break;
				}
			}
		}
		return isValid;
	}
	const [selectedOptions, setSelectedOptions] = useState([]);

	const handleDynamicValue = (type, keyValue, value) => {
		let updatedLoadParams = load_params ? load_params : {};
		if (type === 'options') {
			updatedLoadParams[keyValue] = (value && value.value) ? value.value : null;
		} else if (type === 'multiselect') {
			updatedLoadParams[keyValue] = value ? value.map(option => option.value) : [];
			setSelectedOptions(value);
		} else {
			let inputValue = value ? value.toString().trim() : '';
			inputValue = (inputValue && type === 'number') ? Number(inputValue) : inputValue;
			updatedLoadParams[keyValue] = inputValue;
		}
		setFormDetails({ ...formDetails, load_params: updatedLoadParams });
	}

	const handlePipe = (options) => {
		setFormDetails({ ...formDetails, pipe: (options && options.value) ? options.value : null, pipeId: (options && options.value) ? options.value : null });
	};

	const handleSourcePipe = (options) => {
		setFormDetails({
			...formDetails,
			sourcePipe: (options && options.value) ? options.value : null,
			sourcePipeId: (options && options.value) ? options.value : null
		});
	}

	/* const handleChange = (event) => {
			setFormDetails({ ...formDetails, [event.target.name]: event.target.value });
	}; */

	const handleChange = (event) => {
		const name = event.target.name;

		if (name === "grossWeight" || name === "tareWeight" || name === "lightWeight") {
			const value = event.target.value;
			let grossWeight = Number(formDetails.grossWeight);
			let netWeight = Number(formDetails.loadWeight);
			let tareWeight = Number(formDetails.tareWeight);
			let lightWeight = Number(formDetails.lightWeight);

			switch (name) {
				case "grossWeight":
					grossWeight = value;
					netWeight = Number(grossWeight) - (tareWeight + lightWeight);
					setFormDetails({ ...formDetails, grossWeight, loadWeight: netWeight });
					break;

				case "tareWeight":
					tareWeight = value;
					netWeight = grossWeight - (Number(tareWeight) + lightWeight);
					setFormDetails({ ...formDetails, tareWeight, loadWeight: netWeight });
					break;

				case "lightWeight":
					lightWeight = value;
					netWeight = grossWeight - (tareWeight + Number(lightWeight));
					setFormDetails({ ...formDetails, loadWeight: netWeight, lightWeight });
					break;

				default:
					setFormDetails({ ...formDetails });
					break;
			}
		}
		else
			setFormDetails({ ...formDetails, [event.target.name]: event.target.value });
	};

	// const handleChangeDate = (event) => {
	//     setFormDetails({ ...formDetails, loadTime: event });
	// };

	const findSourceEGDetails = (id, type) => {
		const sourceEG = id ? equipmentGroupList.filter(e => e.equipment_group_uuid === id) : [];
		return sourceEG.length > 0
			? (type === "name" ? sourceEG[0].equipment_group_name : sourceEG[0].equipment_group_uuid) : null;
	};

	const findRegionDetails = (id, type) => {
		const findRegion = id ? geoShapeList.filter(e => e.geo_shape_uuid === id) : [];
		if (!(findRegion.length > 0)) return null;

		if (type === "name") return findRegion[0].geo_region_name;
		if (type === "id") return findRegion[0].geo_region_uuid;
		if (type === "geo_shape_object_uuid") return findRegion[0].geo_shape_object_uuid;
		return null;
	};

	const findShapeDetails = (id, type) => {
		const findRegion = id ? geoShapeList.filter(e => e.geo_shape_uuid === id) : [];
		if (!(findRegion.length > 0)) return null;

		if (type === "name") return findRegion[0].geo_shape_name;
		if (type === "id") return findRegion[0].geo_shape_uuid;
		if (type === "geo_shape_object_uuid") return findRegion[0].geo_shape_object_uuid;
		return null;
	};

	const findCrewDetails = (id, type) => {
		const findCrew = id ? equipmentGroupList.filter(e => e.crew.crew_uuid === id).map(e => e.crew) : [];
		return findCrew.length > 0
			? (type === "name" ? findCrew[0].name : findCrew[0].crew_uuid) : null;
	};

	const findTargetEGDetails = (id, type) => {
		let tarEG = equipmentGroupList.filter(e => e.equipment_group_uuid === id);
		tarEG = (tarEG.length === 0) ? allEGList.filter(e => e.equipment_group_uuid === id) : tarEG;
		return tarEG.length > 0
			? (type === "name" ? tarEG[0].equipment_group_name : tarEG[0].equipment_group_uuid) : null;
	};

	const findTransportEGDetails = (id, type) => {
		const findSemiTruck = id ? equipmentGroupList.filter(e => e.vehicle && e.equipment_group_uuid === id) : [];
		return findSemiTruck.length > 0
			? (type === "name" ? findSemiTruck[0].equipment_group_name : findSemiTruck[0].equipment_group_uuid) : null;
	};

	const findStorageDetails = (id, storageLocationId, type) => {
		const findRegion = id ? (geoShapeList.find(e => e.geo_shape_uuid === id) || {}) : {};
		const findShape = Array.isArray(findRegion.geo_storage_locations) ? (findRegion.geo_storage_locations.find(e => e.geo_storage_location_uuid === storageLocationId) || {}) : {};
		return (type === "name") ? findShape.storage_location_name : findRegion[0].geo_storage_location_uuid;
	};

	const findOperatorDetails = (id, type) => {
		const findOperator = id ? operatorList.filter(e => e.person_uuid === id) : [];
		return findOperator.length > 0
			? (type === "name" ? findOperator[0].person_name : findOperator[0].person_uuid) : null;
	};

	const handleSubmit = () => {
		const validation = validator.validate(formDetails);
		setValidation(validation);
		setIsSubmitted(true);

		if (dynamicFieldVisible && dynamicForm && dynamicForm.length > 0) {
			const isValid = checkValidation(formDetails.load_params);
			if (!isValid) {
				setShowError(true);
				return;
			}
		}


		if (validation.isValid) {
			const { srcEGId, sourceOperatorId, crewId, fieldId, unloadSiteId, storageLocationId, loadWeight, grossWeight, tareWeight, lightWeight, loadActivityId, tarEGId, operatorId, transportEGId, transloadSiteId, transportOperatorId,
				loadTime, pipeId, pipe, scale, /* sourceFieldId, */
				sourceOffloadSite, sourceStorageLocationId, sourcePipe, sourcePipeId, rateScheduleId } = formDetails;
			const isNew = isNewLoad(loadActivityId);

			!isNew && setIsSaving(true);

			let scaleTickets = [];
			let truckIds = [];
			let truck_name = []
			if (scale && scale.length > 0) {
				scale.forEach(element => {
					if (element.ticket) scaleTickets.push(element.ticket);
					if (element.truck) truckIds.push(element.truck);
					if (element.truckName) truck_name.push(element.truckName);
				});
			}

			const params = {
				farm_uuid: farmId,
				load_activity_uuid: loadActivityId,
				offload_geo_shape_uuid: (unloadSiteId && unloadSiteId !== -1) ? unloadSiteId : null,
				offload_geo_region_uuid: findRegionDetails(unloadSiteId, "id"),
				offload_geo_region_name: findRegionDetails(unloadSiteId, "name"),
				offload_geo_shape_name: findShapeDetails(unloadSiteId, "name"),
				field_geo_shape_uuid: (fieldId && fieldId !== -1) ? fieldId : null,
				field_geo_region_uuid: findRegionDetails(fieldId, "id"),
				field_geo_region_name: findRegionDetails(fieldId, "name"),
				field_geo_shape_name: findShapeDetails(fieldId, "name"),
				crew_uuid: findCrewDetails(crewId, "id"),
				crew_name: findCrewDetails(crewId, "name"),
				offload_geo_storage_location_uuid: (storageLocationId && storageLocationId !== -1) ? storageLocationId : null,
				offload_geo_storage_location_name: findStorageDetails(unloadSiteId, storageLocationId, "name"),
				pipe: (unloadSiteId && unloadSiteId !== unknownOption.value && storageLocationId && storageLocationId !== unknownOption.value && pipeId && pipeId !== -1) ? pipe : null,
				pipeId: (unloadSiteId && unloadSiteId !== unknownOption.value && storageLocationId && storageLocationId !== unknownOption.value && pipeId && pipeId !== -1) ? pipeId : null,
				source_equipment_group_uuid: (srcEGId && srcEGId !== -1) ? srcEGId : null,
				source_equipment_group_name: findSourceEGDetails(srcEGId, "name"),
				source_operator_uuid: findOperatorDetails(sourceOperatorId, "id"),
				source_operator_name: findOperatorDetails(sourceOperatorId, "name"),
				load_capacity: unit.fromWeight(loadWeight, 2),
				light_weight: unit.fromWeight(lightWeight, 2),
				load_gross_weight: unit.fromWeight(grossWeight, 2),
				operator_uuid: findOperatorDetails(operatorId, "id"),
				operator_name: findOperatorDetails(operatorId, "name"),
				transport_equipment_group_uuid: findTransportEGDetails(transportEGId, "id"),
				transport_equipment_group_name: findTransportEGDetails(transportEGId, "name"),
				transload_geo_region_uuid: findRegionDetails(transloadSiteId, "id"),
				transload_geo_region_name: findRegionDetails(transloadSiteId, "name"),
				transload_geo_shape_uuid: findShapeDetails(transloadSiteId, "id"),
				transload_geo_shape_name: findShapeDetails(transloadSiteId, "name"),
				transport_operator_uuid: findOperatorDetails(transportOperatorId, "id"),
				transport_operator_name: findOperatorDetails(transportOperatorId, "name"),
				tare_weight: tareWeight ? unit.fromWeight(tareWeight, 2) : 0,
				// variety_name: (seedVariety && seedVariety !== -1) ? seedVariety : null,
				// seed_class: (seedClass && seedClass !== -1) ? seedClass : null,
				// supplier: (seedSupplier && seedSupplier !== -1) ? seedSupplier : null,
				// seed_lot: (seedLot && seedLot !== -1) ? seedLot : null,
				// seed_type: (seedType && seedType !== -1) ? seedType : null,
				// seed_cutting_group: (seedCuttingGroup && seedCuttingGroup !== -1) ? seedCuttingGroup : null,
				scale_ticket: scaleTickets,
				scale_ticket_list: scaleTickets,
				truck_uuid: truckIds,
				truck_list: truckIds,
				truck_name,
				// source_field_uuid: (sourceFieldId && sourceFieldId !== -1) ? sourceFieldId : null,
				// source_field_name: findShapeDetails(sourceFieldId, "name"),
				...dynamicFieldVisible && { load_params: load_params ? load_params : null },
				source_offload_geo_shape_uuid: (sourceOffloadSite && sourceOffloadSite !== -1) ? sourceOffloadSite : null,
				source_offload_geo_region_uuid: findRegionDetails(sourceOffloadSite, "id"),
				source_offload_geo_region_name: findRegionDetails(sourceOffloadSite, "name"),
				source_offload_geo_shape_name: findShapeDetails(sourceOffloadSite, "name"),
				source_offload_geo_storage_location_uuid: (sourceStorageLocationId && sourceStorageLocationId !== -1) ? sourceStorageLocationId : null,
				source_offload_geo_storage_location_name: findStorageDetails(sourceOffloadSite, sourceStorageLocationId, "name"),
				sourcePipe: (sourceOffloadSite && sourceOffloadSite !== -1 && sourceStorageLocationId && sourceStorageLocationId !== -1 && sourcePipeId && sourcePipeId !== -1) ? sourcePipe : null,
				sourcePipeId: (sourceOffloadSite && sourceOffloadSite !== -1 && sourceStorageLocationId && sourceStorageLocationId !== -1 && sourcePipeId && sourcePipeId !== -1) ? sourcePipeId : null,
			};

			if (rateScheduleId && rateScheduleId !== 'unknown_rate') {
				params['rate_info'] = {
					...load.rate_info,
					rate_uuid: rateScheduleId
				}
			}

			if (isCloned) {
				params.target_equipment_group_uuid = findTargetEGDetails(tarEGId, "id");
				params.target_equipment_group_name = findTargetEGDetails(tarEGId, "name");
				params.load_date = moment.utc(toDisplayDateString(loadTime)).format();
			}

			// if (isNew) {
			//     props.actions.editBulkLoad(params.load_activity_uuid, params);
			//     props.onHide();
			// }
			// if (isNew && isCloned) {
			//     props.actions.editBulkLoad(params.load_activity_uuid, { ...params, pipe: Number.isInteger(params.pipe) ? `Pipe ${params.pipe}` : params.pipe });
			//     props.onHide();
			//     setIsSaving(false);
			// }
			// else {
			const postSaveAction = () => {
				const logisticParams = {
					farm_uuid: farmId,
					range_type: range,
					from_date: from,
					to_date: to,
					plan_type_id: selectedPlanTypeId,
					include_events: true
				}
				props.onHide();
				dispatch(getGeoShapeList(farmId));
				dispatch(getLogisticsHistory(logisticParams));
				dispatch(editBulkLoad(params.load_activity_uuid, { ...params, "scale_ticket": params.scale_ticket.toString(), "truck_name": params.truck_name.toString(), pipe: Number.isInteger(params.pipe) ? `Pipe ${params.pipe}` : params.pipe }));				
				dispatch(setIsLoadHistoryReload(true));
			};

			dispatch(editLogisticsLoad(params, isSaved => {
				if (isSaved) {
					dispatch(updateLogisticsLoad(params.load_activity_uuid, params));
					if (params.offload_geo_storage_location_uuid === 'default') {
						const regionShapeId = findRegionDetails(unloadSiteId, "geo_shape_object_uuid");
						if (!regionShapeId) return postSaveAction();

						dispatch(getGeoStorageLocation({ geo_region_shape_uuid: regionShapeId }, (storageLocations) => {
							if (storageLocations && storageLocations.length > 0) {
								params.offload_geo_storage_location_name = storageLocations[0].storage_location_name;
								params.offload_geo_storage_location_uuid = storageLocations[0].geo_storage_location_uuid;
							}
							postSaveAction();
						}));

					} else {
						postSaveAction();
					}
				}
				setIsSaving(false);
			}));
			// }
		}
	};

	const findItem = (list, key, value) => list.find(item => item[key] === value);

	const { srcEGId, sourceOperatorId, crewId, fieldId, unloadSiteId, loadWeight, storageLocationId, tarEGId, /* seedSupplier, */ /* seedVariety, */
		operatorId, pipeId, grossWeight, lightWeight, tareWeight, load_params, scale,
		/* sourceFieldId, */		// transportEGId, transloadSiteId, transportOperatorId, loadTime
		sourceOffloadSite, sourceStorageLocationId, sourcePipeId, rateScheduleId } = formDetails;

	useEffect(() => {
		const fieldList = geoShapeList.filter(e => e.geo_region_type_id === 2 && e.active);
		if (load.field_geo_shape_name !== unknownValue && !findItem(fieldList, "geo_shape_uuid", load.field_geo_shape_uuid)) {
			fieldList.unshift({
				geo_region_name: load.field_geo_region_name,
				geo_region_type_description: "Field",
				geo_region_type_id: 2,
				geo_region_uuid: load.field_geo_region_uuid,
				geo_shape_name: load.field_geo_shape_name,
				geo_shape_uuid: load.field_geo_shape_uuid,
			});
		}

		const offloadList = geoShapeList.filter(e => e.geo_region_type_id === 4 && e.active);
		if (load.offload_geo_shape_name !== unknownValue && !findItem(offloadList, "geo_shape_uuid", load.offload_geo_shape_uuid)) {
			offloadList.unshift({
				geo_region_name: load.offload_geo_region_name,
				geo_region_type_description: "Offload Site",
				geo_region_type_id: 4,
				geo_region_uuid: load.offload_geo_region_uuid,
				geo_shape_name: load.offload_geo_shape_name,
				geo_shape_uuid: load.offload_geo_shape_uuid,
			});
		}

		const transloadList = geoShapeList.filter(e => e.geo_region_type_id === 14 && e.active);
		if (load.transload_geo_shape_name !== unknownValue && !(findItem(transloadList, "geo_shape_uuid", load.transload_geo_shape_uuid))) {
			transloadList.unshift({
				geo_region_name: load.transload_geo_region_name,
				geo_region_type_description: "Transload Site",
				geo_region_type_id: 14,
				geo_region_uuid: load.transload_geo_region_uuid,
				geo_shape_name: load.transload_geo_shape_name,
				geo_shape_uuid: load.transload_geo_shape_uuid,
			});
		}

		const targetOperatorList = operatorList.filter(e => e.alive);
		if (load.operator_name !== unknownValue && !findItem(targetOperatorList, "person_uuid", load.operator_uuid)) {
			targetOperatorList.unshift({
				person_name: load.operator_name,
				person_uuid: load.operator_uuid
			});
		}

		const transloadOperator = operatorList.filter(e => e.alive);
		if (load.operator_name !== unknownValue && !findItem(transloadOperator, "person_uuid", load.operator_uuid)) {
			transloadOperator.unshift({
				person_name: load.operator_name,
				person_uuid: load.operator_uuid
			});
		}

		const sourceList = equipmentGroupList.filter(e => e.alive);
		if (load.source_equipment_group_name !== unknownValue && !findItem(sourceList, "equipment_group_uuid", load.source_equipment_group_uuid)) {
			sourceList.unshift({
				equipment_group_name: load.source_equipment_group_name,
				equipment_group_uuid: load.source_equipment_group_uuid
			});
		}

		const targetEGList = equipmentGroupList.filter(e => e.alive && (e.vehicle || {}).is_truck);
		if (load.target_equipment_group_name !== unknownValue && !findItem(targetEGList, "equipment_group_uuid", load.target_equipment_group_uuid)) {
			targetEGList.unshift({
				equipment_group_name: load.target_equipment_group_name,
				equipment_group_uuid: load.target_equipment_group_uuid
			});
		}

		const transportEGList = equipmentGroupList.filter(e => e.alive && (e.vehicle || {}).is_truck);
		if (load.transport_equipment_group_name !== unknownValue && !findItem(transportEGList, "equipment_group_uuid", load.transport_equipment_group_uuid)) {
			transportEGList.unshift({
				equipment_group_name: load.transport_equipment_group_name,
				equipment_group_uuid: load.transport_equipment_group_uuid
			});
		}

		const list = equipmentGroupList.filter(e => e.crew.name !== '' && e.alive && e.plan_type_id === selectedPlanType).map(e => e.crew);
		if (!findItem(list, "crew_uuid", load.crew_uuid)) {
			list.unshift({
				crew_uuid: load.crew_uuid,
				crew_name: load.crew_name
			});
		}

		const key = "crew_uuid";
		const crewList = [...new Map(list.map(item => [item[key], item])).values()];
		const pipeList = storageLocationId && storageLocationId && storagePipes[storageLocationId] ? storagePipes[storageLocationId] : [];
		const sourcePipeList = sourceStorageLocationId && sourceStorageLocationId && sourceStoragePipes[sourceStorageLocationId] ?
			sourceStoragePipes[sourceStorageLocationId] : [];
		setLoadOptions({
			sourceEGOptions: [
				unknownOption,
				...sourceList.map(e => ({
					value: e.equipment_group_uuid,
					label: e.equipment_group_name
				}))
			],

			fieldOptions: [
				unknownOption,
				...fieldList
					.map(e => ({
						value: e.geo_shape_uuid,
						label: e.geo_shape_name
					}))
			],

			unloadOptions: [
				unknownOption,
				...offloadList
					.map(e => ({
						value: e.geo_shape_uuid,
						label: e.geo_shape_name
					}))
			],
			sourceOffloadOptions: [
				unknownOption,
				...offloadList
					.map(e => ({
						value: e.geo_shape_uuid,
						label: e.geo_shape_name
					}))
			],
			transloadoptions: [
				unknownOption,
				...transloadList
					.map(e => ({
						value: e.geo_shape_uuid,
						label: e.geo_shape_name
					}))
			],

			targetEGOptions: [
				unknownOption,
				...allEG,
				...equipmentGroupList.map(e => ({
					value: e.equipment_group_uuid,
					label: e.equipment_group_name
				}))
			],

			transportEGOptions: [
				unknownOption,
				...equipmentGroupList.filter((e => e.vehicle)).map((e) => ({
					value: e.equipment_group_uuid,
					label: e.equipment_group_name
				}))
			],

			operatorOptions: [
				unknownOption,
				...transloadOperator.map(e => ({
					value: e.person_uuid,
					label: e.person_name
				}))
			],

			transportOperatorOptions: [
				unknownOption,
				...operatorList.map(e => ({
					value: e.person_uuid,
					label: e.person_name
				}))
			],

			storageOptions: [
				unknownOption,
				...storageLocations.map(e => ({
					value: e.geo_storage_location_uuid,
					label: e.storage_location_name
				}))
			],
			sourceStorageOptions: [
				unknownOption,
				...sourceStorageLocations.map(e => ({
					value: e.geo_storage_location_uuid,
					label: e.storage_location_name
				}))
			],
			pipeOptions: [
				unknownOption,
				...pipeList
			],
			sourcePipeOptions: [
				unknownOption,
				...sourcePipeList
			],
			crewOptions: [
				unknownOption,
				...crewList.map(e => ({
					value: e.crew_uuid,
					label: e.crew_name
				}))
			],
			seedVarietyOptions: [
				unknownOption,
				...seedVarietyName.map(e => ({
					value: e,
					label: e
				}))
			],
			// seedClassOptions: [
			// 	unknownOption,
			// 	...numZeroToTen
			// 		.map(e => ({
			// 			value: Number(e),
			// 			label: e
			// 		}))
			// ],
			seedSupplierOptions: [
				unknownOption,
				...seedSupplierName.map(e => ({
					value: e,
					label: e
				}))
			],
			seedLotOptions: [
				unknownOption,
				...seedLots.map(e => ({
					value: e,
					label: e
				}))
			],
			seedTypeOptions: [
				unknownOption,
				...seedTypeList.map(e => ({
					value: e,
					label: e
				}))
			],
			seedCuttingGroupOptions: [
				unknownOption,
				...seedCuttingGroupList.map(e => ({
					value: e,
					label: e
				}))
			],
			scaleTruckOptions: [
				...equipmentGroupList.filter(e => {
					if (e.vehicle.is_truck) return true;
					else return false;
				}).map(e => ({
					value: e.equipment_group_uuid,
					label: e.equipment_group_name
				}))
			]
		});

	}, [show, load, geoShapeList, operatorList, equipmentGroupList, sourceEGList,
		storageLocations, storageLocationId, storagePipes, sourceStorageLocations, sourceStoragePipes]);

	const validations = isSubmitted ? validator.validate(formDetails) : validation;

	useEffect(() => {
		if (!unloadSiteId || unloadSiteId === unknownOption.value) {
			handleSelect({ value: null }, 'storageLocationId');
			setStorageLocations([]);
			return;
		}

		const shape = unloadSiteId ? ((geoShapeList || []).find(shape => shape.geo_shape_uuid === unloadSiteId) || {}) : {};
		if (!shape.geo_shape_object_uuid) return;

		const storageLocations = Array.isArray(shape.geo_storage_locations) ? JSON.parse(JSON.stringify(shape.geo_storage_locations)) : [];
		if (storageLocations.length === 0) {
			storageLocations.push({
				geo_region_shape_uuid: shape.geo_shape_object_uuid,
				geo_storage_location_uuid: "default",
				storage_location_name: "Default",
			});
		}

		const findStorage = findItem(storageLocations, "geo_storage_location_uuid", load.offload_geo_storage_location_uuid);
		if (load.offload_geo_storage_location_name !== unknownValue && load.offload_geo_shape_uuid === unloadSiteId
			&& !findStorage) {
			storageLocations.unshift({
				storage_location_name: load.offload_geo_storage_location_name,
				geo_storage_location_uuid: load.offload_geo_storage_location_uuid
			});
		}

		setStorageLocations(storageLocations);
		if (!findStorage) {
			const defaultOption = storageLocations[0];
			if (load.offload_geo_storage_location_name === unknownValue
				&& load.offload_geo_shape_uuid === unloadSiteId) handleSelect({ value: null }, 'storageLocationId');
			else handleSelect({ value: defaultOption.geo_storage_location_uuid, label: defaultOption.storage_location_name }, 'storageLocationId');
		}
	}, [unloadSiteId, geoShapeList]);
	useEffect(() => {
		if (!sourceOffloadSite || sourceOffloadSite === unknownOption.value) {
			handleSelect({ value: null }, 'sourceStorageLocationId');
			setSourceStorageLocations([]);
			return;
		}

		const shape = sourceOffloadSite ? ((geoShapeList || []).find(shape => shape.geo_shape_uuid === sourceOffloadSite) || {}) : {};
		if (!shape.geo_shape_object_uuid) return;

		const storageLocations = Array.isArray(shape.geo_storage_locations) ? JSON.parse(JSON.stringify(shape.geo_storage_locations)) : [];
		if (storageLocations.length === 0) {
			storageLocations.push({
				geo_region_shape_uuid: shape.geo_shape_object_uuid,
				geo_storage_location_uuid: "default",
				storage_location_name: "Default",
			});
		}

		const findStorage = load ? findItem(storageLocations, "geo_storage_location_uuid", load.offload_geo_storage_location_uuid) : null;
		if (load && load.offload_geo_storage_location_name !== unknownValue && load.offload_geo_shape_uuid === sourceOffloadSite
			&& !findStorage) {
			storageLocations.unshift({
				storage_location_name: load.offload_geo_storage_location_name,
				geo_storage_location_uuid: load.offload_geo_storage_location_uuid
			})
		}

		setSourceStorageLocations(storageLocations);
	}, [sourceOffloadSite, geoShapeList]);
	useEffect(() => {
		const storagePipeListOptions = {};
		storageLocations.forEach(storage => {
			const options = [];
			for (let i = 1; i <= storage.pipe; i++) {
				options.push({
					value: i,
					label: `Pipe ${i}`
				});
			}
			storagePipeListOptions[storage.geo_storage_location_uuid] = options;
		});
		setStoragePipes(storagePipeListOptions);
	}, [storageLocations, storageLocationId, pipeId]);

	useEffect(() => {
		const sourceStoragePipeListOptions = {};
		sourceStorageLocations.forEach(storage => {
			const options = [];
			for (let i = 1; i <= storage.pipe; i++) {
				options.push({
					value: i,
					label: `Pipe ${i}`
				})
			}
			sourceStoragePipeListOptions[storage.geo_storage_location_uuid] = options;
		});
		setSourceStoragePipes(sourceStoragePipeListOptions);
	}, [sourceStorageLocations, sourceStorageLocationId, sourcePipeId]);

	const onAddSection = () => {
		const { scale } = formDetails;
		const newScale = [...scale, { id: uuid(), ticket: null, truck: null }];
		setFormDetails({ ...formDetails, "scale": newScale });
	}

	const onRemoveSection = (scaleId) => {
		const newScale = scale.filter((e) => e.id !== scaleId);
		setFormDetails({ ...formDetails, "scale": newScale });
	}

	const handleScaleSection = (e, key, type, scaleId) => {
		const findScale = scale.find((e) => e.id === scaleId);
		if (findScale && findScale.id) {
			let updatedValue = null;
			if (type === 'options') {
				updatedValue = (e && e.value) ? e.value : null;
			} else {
				let inputValue = e ? e.toString().trim() : '';
				updatedValue = (inputValue && type === 'number') ? Number(inputValue) : inputValue;
			}
			const newScale = [...scale];
			newScale.forEach(element => {
				if (element && element.id === scaleId) {
					element[key] = updatedValue;
					if (key === "truck") {
						element["truckName"] = (e && e.label) ? e.label : null;;
					}
				}
			});
			setFormDetails({ ...formDetails, "scale": newScale });
		}
	}

	const scrollToBottom = () => {
		if (!modalRef.current) return;
		const scrollHeight = modalRef.current.scrollHeight;
		modalRef.current.scrollTo({ top: scrollHeight, behavior: "auto" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [scale]);

	return (<Modal show={show} onHide={onHide} className="modal-form" size="lg">
		<Form className="eg-form">
			<Modal.Header closeButton>
				<Modal.Title><Heading6>Edit Load</Heading6>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body ref={modalRef} className="py-0">
				<Row className="m-0">
					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="crewName">Crew</Form.Label>
							<Form.Select
								id="crewId"
								placeholder="Select Crew"
								name="crewId"
								onChange={(e) => handleSelect(e, 'crewId')}
								value={crewId}
								options={loadOptions.crewOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col>
					{/* <Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="sourceFieldId">Load Origin</Form.Label>
							<Form.Select
								id="sourceFieldId"
								placeholder="Select Load Origin"
								name="sourceFieldId"
								onChange={(e) => handleSelect(e, 'sourceFieldId')}
								value={sourceFieldId}
								options={loadOptions.unloadOptions}
								includeUnknown />
						</Form.Group>
					</Col> */}

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="unloadSite">Source Offload Site</Form.Label>
							<Form.Select
								id="sourceOffloadSite"
								placeholder="Select Source Offload Site"
								name="sourceOffloadSite"
								onChange={(e) => handleSelect(e, 'sourceOffloadSite')}
								value={sourceOffloadSite}
								options={loadOptions.sourceOffloadOptions}
								includeUnknown />
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="storage">Source Storage</Form.Label>
							<Form.Select
								id="sourceStorageLocationId"
								placeholder="Select Source Storage"
								name="sourceStorageLocationId"
								onChange={(e) => handleSelect(e, 'sourceStorageLocationId')}
								value={sourceStorageLocationId}
								options={loadOptions.sourceStorageOptions}
								includeUnknown />
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="sourcePipe">Source Pipe</Form.Label>
							<Form.Select
								id="sourcePipe"
								placeholder="Select Source Pipe"
								name="sourcePipe"
								onChange={(e) => handleSourcePipe(e)}
								value={sourcePipeId}
								options={loadOptions.sourcePipeOptions} />
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="unloadSite">Offload Site</Form.Label>
							<Form.Select
								id="unloadSite"
								placeholder="Select Offload Site"
								name="unloadSiteId"
								onChange={(e) => handleSelect(e, 'unloadSiteId')}
								value={unloadSiteId}
								options={loadOptions.unloadOptions}
								sortType="asc"
								includeUnknown />

							{/* <Form.Select
                                id="unloadSite"
                                placeholder="Select Offload Site"
                                name="unloadSiteId"
                                onChange={(e) => {
                                    setFormDetails({ ...formDetails, unloadSiteId: (e && e.value) ? e.value : null });
                                }}
                                value={unloadSiteId}
                                options={loadOptions.unloadOptions} /> */}
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="storage">Storage</Form.Label>
							<Form.Select
								id="storageLocationId"
								placeholder="Select Storage"
								name="storageLocationId"
								onChange={(e) => handleSelect(e, 'storageLocationId')}
								value={storageLocationId}
								options={loadOptions.storageOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="pipe">Pipe</Form.Label>
							<Form.Select
								id="pipe"
								placeholder="Select Pipe"
								name="pipe"
								onChange={(e) => handlePipe(e)}
								value={pipeId}
								options={loadOptions.pipeOptions} />
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="srcEqGroup">Source EG</Form.Label>
							<Form.Select
								id="srcEqGroup"
								placeholder="Select Source Equipment Group"
								name="srcEGId"
								onChange={(e) => handleSelect(e, 'srcEGId')}
								value={srcEGId}
								options={loadOptions.sourceEGOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="tarEqGroup">Target EG</Form.Label>
							<Form.Select
								disabled
								id="tarEqGroup"
								placeholder="Select Target Equipment Group"
								name="tarEGId"
								onChange={(e) => handleSelect(e, 'tarEGId')}
								value={tarEGId}
								options={loadOptions.targetEGOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col>

					{/* <Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="crewName">Seed Supplier</Form.Label>
							<Form.Select
								id="seedSupplier"
								placeholder="Select supplier"
								name="seedSupplier"
								onChange={(e) => handleSelect(e, 'seedSupplier')}
								value={seedSupplier}
								options={loadOptions.seedSupplierOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col> */}

					{/* <Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="crewName">Seed Variety</Form.Label>
							<Form.Select
								id="seedVariety"
								placeholder="Select variety"
								name="seedVariety"
								onChange={(e) => handleSelect(e, 'seedVariety')}
								value={seedVariety}
								options={loadOptions.seedVarietyOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col> */}

					{/* <Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="crewName">Seed Class</Form.Label>
							<Form.Select
								id="seedClass"
								placeholder="Select class"
								name="seedClass"
								onChange={(e) => handleSelect(e, 'seedClass')}
								value={Number(seedClass)}
								options={loadOptions.seedClassOptions}
								includeUnknown />
						</Form.Group>
					</Col> */}

					{/* <Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="crewName">Seed Type</Form.Label>
							<Form.Select
								id="seedType"
								placeholder="Select variety"
								name="seedType"
								onChange={(e) => handleSelect(e, 'seedType')}
								value={seedType}
								options={loadOptions.seedTypeOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col> */}


					{/* <Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="crewName">Seed Lot</Form.Label>
							<Form.Select
								id="seedLot"
								placeholder="Select Seed Lot"
								name="seedLot"
								onChange={(e) => handleSelect(e, 'seedLot')}
								value={seedLot}
								options={loadOptions.seedLotOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col> */}

					{/* <Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="crewName">Seed Cutting Group</Form.Label>
							<Form.Select
								id="seedCuttingGroup"
								placeholder="Select variety"
								name="seedCuttingGroup"
								onChange={(e) => handleSelect(e, 'seedCuttingGroup')}
								value={seedCuttingGroup}
								options={loadOptions.seedCuttingGroupOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col> */}

					{/* <Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="txtName">
								Average Seed Size
							</Form.Label>
							<Form.Control
								id="averageSeedSize"
								value={averageSeedSize}
								onChange={handleChange}
								min={1}
								max={1000000}
								type="number"
								placeholder="Enter Average Seed Size"
								className="form-control"
								autoComplete="off"
								name="averageSeedSize" />
						</Form.Group>
					</Col> */}

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Validator validation={validations.grossWeight}>
								<Form.Label htmlFor="txtName">
									Gross Weight ({capitalize(weightShortName)})<Validator.Mand />
								</Form.Label>
								<Form.Control
									id="grossWeight"
									value={grossWeight}
									onChange={handleChange}
									min={1}
									max={1000000}
									type="number"
									placeholder="Enter Gross weight"
									className="form-control"
									autoComplete="off"
									name="grossWeight" />
								<Validator.Text />
							</Validator>
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="txtName">
								Light Weight ({capitalize(weightShortName)})
							</Form.Label>
							<Form.Control
								id="lightWeight"
								value={lightWeight}
								onChange={handleChange}
								min={1}
								max={1000000}
								type="number"
								placeholder="Enter light weight"
								className="form-control"
								autoComplete="off"
								name="lightWeight" />
							<Validator.Text />
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="tareWeight">
								Tare Weight ({capitalize(weightShortName)})
							</Form.Label>
							<Form.Control
								id="tareweight"
								value={tareWeight}
								onChange={handleChange}
								min={0}
								type="number"
								placeholder='Enter Tare Weight'
								className='form-control'
								autoComplete='off'
								name='tareWeight'
							/>
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Validator validation={validations.loadWeight}>
								<Form.Label htmlFor="txtName">
									Load Weight ({capitalize(weightShortName)})<Validator.Mand />
								</Form.Label>
								<Form.Control
									id="loadWeight"
									readOnly={true}
									value={loadWeight}
									onChange={handleChange}
									min={1}
									max={1000000}
									type="number"
									placeholder="Enter load weight"
									className="form-control"
									autoComplete="off"
									name="loadWeight" />
								<Validator.Text />
							</Validator>
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="operator">Source EG Operator</Form.Label>
							<Form.Select
								id="operator"
								placeholder="Select Operator"
								name="sourceOperatorId"
								onChange={(e) => handleSelect(e, 'sourceOperatorId')}
								value={sourceOperatorId}
								options={loadOptions.operatorOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="operator">Target EG Operator</Form.Label>
							<Form.Select
								id="operator"
								placeholder="Select Operator"
								name="operatorId"
								onChange={(e) => handleSelect(e, 'operatorId')}
								value={operatorId}
								options={loadOptions.operatorOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col>

					<Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="rate">Select Rate</Form.Label>
							<Form.Select
								id="rate"
								placeholder="Select Field"
								name="rateScheduleId"
								onChange={(e) => handleSelect(e, "rateScheduleId")}
								value={rateScheduleId}
								options={getRateList}
							/>
						</Form.Group>
					</Col>

					{false && <Col sm={6} md={6} lg={6}>
						<Form.Group>
							<Form.Label htmlFor="field">Field</Form.Label>
							<Form.Select
								id="field"
								placeholder="Select Field"
								name="fieldId"
								onChange={(e) => handleSelect(e, 'fieldId')}
								value={fieldId}
								options={loadOptions.fieldOptions}
								sortType="asc"
								includeUnknown />
						</Form.Group>
					</Col>}
					<>
						{show && dynamicForm && dynamicForm.length ?
							dynamicForm.map(e => {
								const keyValue = e.key;
								const value = load_params ?
									load_params.hasOwnProperty(keyValue) ? load_params[keyValue] : null : null

								const multipleValues = e.isMulti && e.default_values && Array.isArray(e.default_values)
									? e.default_values.map(value => ({
										label: value,
										value: value,
									}))
									: []

								const FinalValue = (e.isMulti && multipleValues && multipleValues.length > 0) && e.options.filter((e) =>
									multipleValues.some((item) => item.value === e.value)
								)
								const defaultValues = load_params && load_params.hasOwnProperty(keyValue)
									? (keyValue === 'load_debris' && Array.isArray(load_params[keyValue]) && load_params[keyValue].length > 0)
										? load_params[keyValue].map(value => ({
											label: value,
											value: value,
										}))
										: FinalValue
									: FinalValue
								return (
									(e.type && e.type === 'multiselect' && e.visible && e.isMulti) ?
										<Col className='col-6' key={keyValue}>
											<Form.Label htmlFor={keyValue}>{e.label ? e.label : ''}
												{(e.required) ? <small className="error-mand ml-1">*</small> : null}</Form.Label>
											<MultiSelect
												closeMenuOnSelect={false}
												defaultValue={defaultValues}
												isMulti={true}
												isClearable={true}
												placeholder={e.label}
												value={selectedOptions}
												options={e.options}
												designType="checkbox"
												onChange={(i) => handleDynamicValue(e.type, keyValue, i)}
											/>
											{((showError) && e.required && !value) ?
												<small className="form-text text-error">{e.label} is required.</small> : null}
										</Col> :
										(e.type && e.type === 'options' && e.visible) ?
											<Col className='col-6 pb-2' key={keyValue}>
												<Form.Group>
													<Form.Label htmlFor={keyValue}>{e.label ? e.label : ''}
														{(e.required) ? <small className="error-mand ml-1">*</small> : null}</Form.Label>
													<Form.Select
														id={keyValue}
														placeholder={`Select ${e.label}`}
														name={keyValue}
														onChange={(i) => handleDynamicValue(e.type, keyValue, i)}
														value={value}
														options={e.options} />
													{((showError) && e.required && !value) ?
														<small className="form-text text-error">{e.label} is required.</small> : null}
												</Form.Group>
											</Col>
											: ((e.type && e.visible) && (e.type === 'text' || e.type === 'number')) ?
												<Col className='col-6' key={keyValue}>
													<Form.Group>
														<Form.Label htmlFor={keyValue}>{e.label ? e.label : ''}
															{(e.required) ? <small className="error-mand ml-1">*</small> : null}</Form.Label>
														<Form.Control
															id={keyValue}
															value={value ? value : ''}
															type={e.type}
															placeholder={`Enter ${e.label}`}
															className="form-control"
															autoComplete="off"
															name={keyValue}
															onChange={i => handleDynamicValue(e.type, keyValue, i.target.value)}
														/>
														{((showError) && e.required && !value)
															? <small className="form-text text-error">{e.label} is required.</small> : null}

														{(value && e.rules && e.rules.hasOwnProperty('max_length')
															&& (value.length > e.rules.max_length)) ?
															<small className="form-text text-error">Length can not exceed {e.rules.max_length}.</small> : null}
													</Form.Group>
												</Col>
												: null
								)
							}
							) : null}
					</>
					{/* <Col sm={6} md={6} lg={6}>
                        <Form.Group>
                            <Form.Label htmlFor="transloadSite">Transload Site</Form.Label>
                            <Form.Select
                                id="transloadSite"
                                placeholder="Select Field"
                                name="transloadSiteId"
                                onChange={(e) => handleSelect(e, 'transloadSiteId')}
                                value={transloadSiteId}
                                options={loadOptions.transloadoptions}
                                sortType="asc"
                                includeUnknown />
                        </Form.Group>
                    </Col> */}
					{/* <Col sm={6} md={6} lg={6}>
                        <Form.Group>
                            <Form.Label htmlFor="Transport Equipment Group">Transport EG</Form.Label>
                            <Form.Select
                                id="Transport Equipment Group"
                                placeholder="Select Transport Equipment Group"
                                name="transportEGId"
                                onChange={(e) => handleSelect(e, 'transportEGId')}
                                value={transportEGId}
                                options={loadOptions.transportEGOptions}
                                sortType="asc"
                                includeUnknown />

                        </Form.Group>
                    </Col>
                    <Col sm={6} md={6} lg={6}>
                        <Form.Group>
                            <Form.Label htmlFor="operator">Transport EG Operator</Form.Label>
                            <Form.Select
                                id="operator"
                                placeholder="Select Operator"
                                name="transportOperatorId"
                                onChange={(e) => handleSelect(e, 'transportOperatorId')}
                                value={transportOperatorId}
                                options={loadOptions.transportOperatorOptions}
                                sortType="asc"
                                includeUnknown />
                        </Form.Group>
                    </Col> */}

					{/* {isCloned &&
                        <Col sm={6} md={6} lg={6}>
                            <Form.Group>
                                <Validator validation={validations.loadTime}>
                                    <Form.Label htmlFor="loadTime">Load Time<Validator.Mand /></Form.Label>
                                    <Datetime
                                        id="loadTime"
                                        placeholder="Select load time"
                                        name="loadTime"
                                        closeOnSelect={true}
                                        onChange={handleChangeDate}
                                        value={loadTime}
                                        selected={loadTime}
                                        autoComplete="off" />
                                    <Validator.Text />
                                </Validator>
                            </Form.Group>
                        </Col>} */}

				</Row>
				<>
					{scale && scale.length > 0 ?
						scale.map(((e, index) => {
							return (
								<Row key={e.id} className={`m-0 scale-ticket-card ${index === 0 ? '' : 'scale-ticket-container'} ${index === 1 && scale.length === 2 ? "scale-first-child" : ""}`}>
									<Col md={6} key={`ticket${index}`}>
										<Form.Group>
											<Form.Label htmlFor="ticket">
												<div className={`scale-ticket-btn add-load-btn m-auto d-flex g-2`} key={`button${index}`}>
													<span>Scale Ticket</span>
													<Button size="sm" variant="default" className={`btn-load-queue btn-sm p-0 ${isMultiTicket ? '' : 'disabled'}`}>
														{(index === 0) ? <Icon className="fa fa-plus-circle text-success" onClick={() => onAddSection()} />
															: <Icon className="fa fa-minus-circle text-danger" onClick={() => onRemoveSection(e.id)} />}
													</Button>
												</div>
											</Form.Label>
											<Form.Control
												id="ticket"
												value={e['ticket'] || null}
												onChange={(elm) => handleScaleSection(elm.target.value, 'ticket', 'number', e.id)}
												type="number"
												placeholder="Enter Scale Ticket"
												className="form-control"
												autoComplete="off"
												name="ticket" />
										</Form.Group>
									</Col>
									<Col md={6} key={`truck${index}`}>
										<Form.Group>
											<Form.Label htmlFor="truck">Truck</Form.Label>
											<Form.Select
												disabled={isMultiTicket ? false : true}
												id="truck"
												placeholder="Select Truck"
												name="truck"
												onChange={(elm) => handleScaleSection(elm, 'truck', 'options', e.id)}
												value={e['truck'] || null}
												options={loadOptions.scaleTruckOptions}
											/>
										</Form.Group>
									</Col>
								</Row>
							);
						}))
						: null}
				</>
			</Modal.Body>
			<Modal.Footer>
				<Button size="sm" variant="danger" onClick={onHide}>
					<Icon className="fa fa-ban" /> Cancel
				</Button>
				<Button size="sm" variant="primary" onClick={handleSubmit}>
					<Icon className="fa fa-floppy-o" /> {isSaving ? "Saving..." : "Save"}
				</Button>
			</Modal.Footer>
		</Form>
	</Modal >);
};

export default LoadForm;